import axios from "axios";
import { API_BASE as SBIN_API_BASE } from "./base";

const API = SBIN_API_BASE;
const DEBUG_KEY = "salonspeak:debug-http";
const DEFAULT_MODULE = "english";
const MODULE_PATTERN = /^[a-z0-9_-]{2,32}$/;

const shouldDebugSalonSpeak = () => {
  try {
    return process.env.NODE_ENV !== "production" || localStorage.getItem(DEBUG_KEY) === "1";
  } catch (err) {
    return process.env.NODE_ENV !== "production";
  }
};

const logSalonSpeak = (label, payload) => {
  if (!shouldDebugSalonSpeak()) return;
  const stamp = new Date().toISOString();
  console.log(`[SalonSpeak API] ${stamp} ${label}`, payload);
};

const authHeaders = (token) => {
  const authToken = token || null;
  return authToken
    ? {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json",
      }
    : { "Content-Type": "application/json" };
};

const normalizeModule = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  if (!normalized) return DEFAULT_MODULE;
  if (!MODULE_PATTERN.test(normalized)) return DEFAULT_MODULE;
  return normalized;
};

const resolveModule = (options) => {
  if (typeof options === "string") {
    return normalizeModule(options);
  }
  if (options && typeof options === "object") {
    return normalizeModule(options.module);
  }
  return DEFAULT_MODULE;
};

const withModuleParams = (params = {}, options) => {
  const fallbackOptions =
    options ??
    (params && typeof params === "object" && Object.prototype.hasOwnProperty.call(params, "module")
      ? { module: params.module }
      : undefined);
  return {
    ...params,
    module: resolveModule(fallbackOptions),
  };
};

export async function getSalonSpeakMe(token, options) {
  const module = resolveModule(options);
  const request = {
    method: "GET",
    url: `${API}/salonspeak/me`,
    params: { module },
  };
  logSalonSpeak("REQUEST", request);
  try {
    const res = await axios.get(request.url, {
      headers: authHeaders(token),
      params: request.params,
    });
    logSalonSpeak("RESPONSE", {
      ...request,
      status: res.status,
      data: res.data,
    });
    return res.data;
  } catch (err) {
    logSalonSpeak("ERROR", {
      ...request,
      status: err?.response?.status || null,
      data: err?.response?.data || null,
      message: err?.message || "Request failed",
    });
    throw err;
  }
}

export async function updateSalonSpeakMe(payload, token, options) {
  const module = resolveModule(options);
  const request = {
    method: "PUT",
    url: `${API}/salonspeak/me`,
    params: { module },
    payload,
  };
  logSalonSpeak("REQUEST", request);
  try {
    const res = await axios.put(request.url, payload, {
      headers: authHeaders(token),
      params: request.params,
    });
    logSalonSpeak("RESPONSE", {
      ...request,
      status: res.status,
      data: res.data,
    });
    return res.data;
  } catch (err) {
    logSalonSpeak("ERROR", {
      ...request,
      status: err?.response?.status || null,
      data: err?.response?.data || null,
      message: err?.message || "Request failed",
    });
    throw err;
  }
}

export async function createSalonSpeakAttempt(payload, token, options) {
  const module = resolveModule(options);
  const request = {
    method: "POST",
    url: `${API}/salonspeak/attempts`,
    params: { module },
    payload,
  };
  logSalonSpeak("REQUEST", request);
  try {
    const res = await axios.post(request.url, payload, {
      headers: authHeaders(token),
      params: request.params,
    });
    logSalonSpeak("RESPONSE", {
      ...request,
      status: res.status,
      data: res.data,
    });
    return res.data;
  } catch (err) {
    logSalonSpeak("ERROR", {
      ...request,
      status: err?.response?.status || null,
      data: err?.response?.data || null,
      message: err?.message || "Request failed",
    });
    throw err;
  }
}

export async function listSalonSpeakAttempts(params = {}, token, options) {
  const requestParams = withModuleParams(params, options);
  const request = {
    method: "GET",
    url: `${API}/salonspeak/attempts`,
    params: requestParams,
  };
  logSalonSpeak("REQUEST", request);
  try {
    const res = await axios.get(request.url, {
      params: requestParams,
      headers: authHeaders(token),
    });
    logSalonSpeak("RESPONSE", {
      ...request,
      status: res.status,
      data: res.data,
    });
    return res.data;
  } catch (err) {
    logSalonSpeak("ERROR", {
      ...request,
      status: err?.response?.status || null,
      data: err?.response?.data || null,
      message: err?.message || "Request failed",
    });
    throw err;
  }
}

export async function getSalonSpeakAttempts(params = {}, token, options) {
  return listSalonSpeakAttempts(params, token, options);
}

export async function getSalonSpeakFavorites(token, options) {
  const module = resolveModule(options);
  const res = await axios.get(`${API}/salonspeak/favorites`, {
    params: { module },
    headers: authHeaders(token),
  });
  return res.data;
}

export async function listSalonSpeakFavorites(token, options) {
  return getSalonSpeakFavorites(token, options);
}

export async function addSalonSpeakFavorite(payload, token, options) {
  const module = resolveModule(options);
  const res = await axios.post(`${API}/salonspeak/favorites`, payload, {
    params: { module },
    headers: authHeaders(token),
  });
  return res.data;
}

export async function removeSalonSpeakFavorite(favorite_type, content_id, token, options) {
  const module = resolveModule(options);
  const res = await axios.delete(`${API}/salonspeak/favorites`, {
    params: { favorite_type, content_id, module },
    headers: authHeaders(token),
  });
  return res.data;
}

export async function requestSalonSpeakTtsAudio(payload, token) {
  const request = {
    method: "POST",
    url: `${API}/salonspeak/tts`,
    payload,
  };
  logSalonSpeak("REQUEST", request);
  try {
    const res = await axios.post(request.url, payload, {
      headers: authHeaders(token),
      responseType: "blob",
    });
    logSalonSpeak("RESPONSE", {
      ...request,
      status: res.status,
      contentType: res.headers?.["content-type"] || null,
      size: res.data?.size ?? null,
    });
    return res.data;
  } catch (err) {
    logSalonSpeak("ERROR", {
      ...request,
      status: err?.response?.status || null,
      data: err?.response?.data || null,
      message: err?.message || "Request failed",
    });
    throw err;
  }
}
